import { db } from '../lib/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface ErrorLog {
  message: string;
  stack?: string;
  severity: ErrorSeverity;
  userId?: string;
  action?: string;
  timestamp: Date;
  metadata?: Record<string, any>;
}

class ErrorLoggingService {
  private static sanitizeError(error: any): string {
    // Remove sensitive information from error messages
    const errorString = error.toString();
    return errorString
      .replace(/password=["'].*?["']/gi, 'password=[REDACTED]')
      .replace(/token=["'].*?["']/gi, 'token=[REDACTED]')
      .replace(/key=["'].*?["']/gi, 'key=[REDACTED]')
      .replace(/secret=["'].*?["']/gi, 'secret=[REDACTED]');
  }

  private static sanitizeStack(stack?: string): string | undefined {
    if (!stack) return undefined;
    // Remove sensitive paths and information from stack traces
    return stack
      .replace(/\/home\/.*?\//g, '/[REDACTED]/')
      .replace(/\/Users\/.*?\//g, '/[REDACTED]/')
      .replace(/\/root\/.*?\//g, '/[REDACTED]/');
  }

  static async logError(
    error: Error | string,
    severity: ErrorSeverity = 'medium',
    userId?: string,
    action?: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    try {
      const errorLog: ErrorLog = {
        message: this.sanitizeError(error),
        stack: this.sanitizeStack(error instanceof Error ? error.stack : undefined),
        severity,
        userId,
        action,
        timestamp: new Date(),
        metadata: this.sanitizeMetadata(metadata)
      };

      // Log to Firestore
      await addDoc(collection(db, 'errorLogs'), {
        ...errorLog,
        timestamp: serverTimestamp()
      });

      // Log to console in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Error Log:', errorLog);
      }

      // For critical errors, you might want to add additional handling
      if (severity === 'critical') {
        // Implement critical error handling (e.g., notify administrators)
        this.handleCriticalError(errorLog);
      }
    } catch (loggingError) {
      // Fallback logging if the main logging fails
      console.error('Failed to log error:', loggingError);
    }
  }

  private static sanitizeMetadata(metadata?: Record<string, any>): Record<string, any> | undefined {
    if (!metadata) return undefined;

    const sensitiveFields = ['password', 'token', 'key', 'secret', 'apiKey', 'auth'];
    const sanitized = { ...metadata };

    for (const field of sensitiveFields) {
      if (field in sanitized) {
        sanitized[field] = '[REDACTED]';
      }
    }

    return sanitized;
  }

  private static handleCriticalError(errorLog: ErrorLog): void {
    // Implement critical error handling
    // This could include:
    // - Sending notifications to administrators
    // - Triggering incident response procedures
    // - Alerting monitoring systems
    console.error('CRITICAL ERROR:', errorLog);
  }
}

export default ErrorLoggingService; 